"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveAppPageExposureProfile = resolveAppPageExposureProfile;
exports.appPageIndexable = appPageIndexable;
exports.appPageSnapshotAudience = appPageSnapshotAudience;
exports.withAppPageExposures = withAppPageExposures;
exports.withCompiledAppArtifact = withCompiledAppArtifact;
exports.appRouteSeoExposureIssues = appRouteSeoExposureIssues;
exports.appPageExposureProfiles = appPageExposureProfiles;
exports.getAppPageExposureIssues = getAppPageExposureIssues;
exports.resolveAppDefinitionExposureProfile = resolveAppDefinitionExposureProfile;
exports.getAppDefinitionExposureIssues = getAppDefinitionExposureIssues;
exports.appPageExposureIssueMessages = appPageExposureIssueMessages;
const app_access_vocabulary_1 = require("./app-access-vocabulary");
const app_base_config_1 = require("./app-base-config");
const app_base_contract_1 = require("./app-base-contract");
const app_page_access_1 = require("./app-page-access");
const app_page_delivery_1 = require("./app-page-delivery");
/**
 * Delivery profile of each App page from its page rights (Apps-Rechte 1b).
 * A page exclusively for internal members runs live; a page portal
 * persons open is a portal page; a page open to visitors is static or
 * interactive as its rights allow; a page only for members (or for nobody)
 * needs sign-in. Container kind never grants or rewrites rights. Who enters it decides
 * appPageEntryAdmission.
 */
function resolveAppPageExposureProfile(input) {
    const { appBaseConfig, route } = input;
    const section = appBaseConfig.pageAccess;
    if ((0, app_page_access_1.appPageAudienceAccess)(section, route.path, 'portal').active) {
        return 'memberPortal';
    }
    if ((0, app_page_access_1.appPageAudienceAccess)(section, route.path, 'public').active) {
        // A live public page talks to the backend on purpose; else the CDN
        // serves it wherever its rights allow.
        return !(0, app_page_access_1.appPageRefreshLive)(section, route.path, 'public') &&
            (0, app_page_delivery_1.resolveAppPageDelivery)({ appBaseConfig, route }) === 'static'
            ? 'publicStatic'
            : 'publicInteractive';
    }
    return (0, app_page_access_1.appPageAudienceAccess)(section, route.path, 'internal').active
        ? 'internalLive'
        : 'memberPortal';
}
/**
 * Whether search engines may index a page: the robots rule of the profile
 * its page rights give it. Delivery, route snapshots and SEO ask here.
 */
function appPageIndexable(input) {
    return (0, app_base_contract_1.deriveAppBaseExposureProfile)(resolveAppPageExposureProfile(input))
        .robotsIndex;
}
/**
 * The audience route snapshots carry for a page: public when search engines
 * may index it, portal when portal persons open it, otherwise internal.
 * Sitemap, robots.txt, llms.txt and the SEO texts read it.
 */
function appPageSnapshotAudience(input) {
    const { appBaseConfig } = input;
    const path = (0, app_base_contract_1.normalizeAppRoutePath)(input.routePath);
    const route = appBaseConfig.appBase?.routes.find(candidate => (0, app_base_contract_1.normalizeAppRoutePath)(candidate.path) === path);
    if (route && appPageIndexable({ appBaseConfig, route }))
        return 'public';
    return (0, app_page_access_1.appPortalReachesAnyPage)(appBaseConfig.pageAccess, [path])
        ? 'portal'
        : 'internal';
}
/** The live data policy of a delivery whose pages all run economically. */
const APP_DELIVERY_WITHOUT_LIVE_DATA = (0, app_base_contract_1.deriveAppBaseExposureProfile)('publicStatic').delivery.liveData;
/**
 * The container with the delivery of every App, its cache rules, its
 * capabilities and the SEO of every App page derived from the page rights.
 * Each App runs with the strongest profile of its pages; each App page gets
 * the SEO of its own profile. The compiled artifact carries the same manifest.
 */
function withAppPageExposures(appBaseConfig) {
    const manifest = appBaseConfig.appBase;
    if (!manifest?.appDeliveries)
        return appBaseConfig;
    const routesByPath = new Map(manifest.routes.map(route => [route.path, route]));
    const profileOfPage = (routePath) => {
        const route = routesByPath.get(routePath);
        if (!route) {
            throw new Error(`App has no page ${routePath}`);
        }
        return resolveAppPageExposureProfile({ appBaseConfig, route });
    };
    const appDeliveries = manifest.appDeliveries.map((app) => {
        // The stronger profile wins the delivery of a whole App.
        const profile = (0, app_base_contract_1.strongestAppBaseExposureProfile)(app.routePaths.map(profileOfPage), 'internalLive');
        const delivery = (0, app_base_contract_1.deriveAppBaseExposureProfile)(profile).delivery;
        return {
            ...app,
            delivery: {
                ...delivery,
                routePolicies: [],
                // Live data only where a page of the App runs live; the event
                // stream checks the page itself (appPageLiveData).
                liveData: app.routePaths.some(path => (0, app_page_access_1.appPageLiveData)(appBaseConfig.pageAccess, path))
                    ? delivery.liveData
                    : APP_DELIVERY_WITHOUT_LIVE_DATA,
            },
        };
    });
    // The shell has to deliver every page it holds, its own start page
    // included: it is as open as its most open page. A page nobody opens adds
    // nothing; without such pages the shell opens to nobody outside.
    const shellProfile = (0, app_base_contract_1.openestAppBaseExposureProfile)(manifest.routes
        .filter(route => app_access_vocabulary_1.AppAccessAudienceSchema.options.some(audience => (0, app_page_access_1.appPageAudienceReachesAnyPage)(appBaseConfig.pageAccess, [route.path], audience)))
        .map(route => profileOfPage(route.path)), 'internalLive');
    const shellExposure = (0, app_base_contract_1.deriveAppBaseExposureProfile)(shellProfile);
    const routeSeo = manifest.routeSeo.map(item => {
        if (item.owner.kind === 'shell') {
            return (0, app_base_contract_1.appShellRouteSeoForExposure)(item, shellProfile);
        }
        if (item.owner.kind !== 'app')
            return item;
        const definition = appBaseConfig.appDefinitions[item.owner.appId];
        if (!definition) {
            throw new Error(`App route SEO ${item.id} names App Module ${item.owner.appId} without a definition`);
        }
        return (0, app_base_contract_1.appRouteSeoForExposure)({
            id: item.id,
            routePath: item.routePath,
            appLevel: item.appLevel,
            owner: item.owner,
            appSeo: definition.seo,
            profile: profileOfPage(item.routePath),
        });
    });
    // Generated capabilities follow the deliveries: drop them so the rebuild
    // writes them anew instead of keeping the declarations of the old profile.
    const generatedCapabilityIds = new Set((0, app_base_contract_1.buildAppBaseCapabilityDeclarations)({ ...manifest, capabilities: [] }).map(capability => capability.id));
    const nextManifest = (0, app_base_contract_1.withAppBaseInteractionContract)({
        ...manifest,
        delivery: (0, app_base_contract_1.withRequiredAppBaseDeliveryRoutePolicies)({
            delivery: shellExposure.delivery,
            appDeliveries,
            dataSourceKeys: manifest.dataSourceKeys,
            routes: manifest.routes,
            actions: manifest.actions,
        }),
        appDeliveries,
        routeSeo,
        capabilities: (manifest.capabilities ?? []).filter(capability => !generatedCapabilityIds.has(capability.id)),
    });
    return app_base_config_1.AppBaseConfigSchema.parse({
        ...appBaseConfig,
        appBase: nextManifest,
        ...(appBaseConfig.appArtifact
            ? {
                appArtifact: { ...appBaseConfig.appArtifact, manifest: nextManifest },
            }
            : {}),
    });
}
/**
 * The one composition of a container with a newly compiled artifact: the
 * page rights follow the pages (kept for pages that had rights, the App's
 * recommendation for new ones), then every App takes the delivery its page
 * rights call for. `previous` null is a new installation without a former
 * state.
 */
function withCompiledAppArtifact(input) {
    const { appBaseConfig, previous, artifact } = input;
    const routes = artifact.manifest.routes;
    return withAppPageExposures(app_base_config_1.AppBaseConfigSchema.parse({
        ...appBaseConfig,
        pageAccess: (0, app_page_access_1.appPageAccessForRoutes)({
            section: previous?.pageAccess,
            previousRoutePaths: previous
                ? (0, app_page_access_1.appPagesKeepingRights)({
                    routes: previous.appBase?.routes ?? [],
                    routeSeo: previous.appBase?.routeSeo ?? [],
                    section: previous.pageAccess,
                })
                : [],
            routes,
            previousPathByPath: input.previousPathByPath,
        }),
        appBase: artifact.manifest,
        appArtifact: artifact,
    }));
}
/**
 * App route SEO that indexes a page its page rights keep out of search: one
 * sentence per such page. Only App pages follow page rights
 * (withAppPageExposures); the shell page keeps the container's contract.
 */
function appRouteSeoExposureIssues(appBaseConfig) {
    const manifest = appBaseConfig.appBase;
    if (!manifest)
        return [];
    return manifest.routeSeo.flatMap(item => {
        if (item.owner.kind !== 'app' || !item.indexableByDefault)
            return [];
        const route = manifest.routes.find(candidate => candidate.path === item.routePath);
        // A route SEO without its page is the manifest schema's issue.
        if (!route)
            return [];
        return appPageIndexable({ appBaseConfig, route })
            ? []
            : [
                `App route SEO can be indexable only for pages open to visitors: ${item.routePath}`,
            ];
    });
}
/** The profile the page rights give every page of the container, by path. */
function appPageExposureProfiles(appBaseConfig) {
    return new Map((appBaseConfig.appBase?.routes ?? []).map(route => [
        route.path,
        resolveAppPageExposureProfile({ appBaseConfig, route }),
    ]));
}
/**
 * Whether the Apps of a container fit the rights of their pages: the rules of
 * getAppVisibilityCompatibilityIssues with the profile of each page, plus the
 * rules of the pages themselves. A page that shows an Agent Action needs
 * members who act internally on its tables there; a page that leads a
 * command changing own rows is a portal page; a page that leads a command
 * adding row groups is not an internal one.
 */
function getAppPageExposureIssues(appBaseConfig) {
    const composition = appBaseConfig.appComposition;
    const manifest = appBaseConfig.appBase;
    if (!composition || !manifest)
        return [];
    const profiles = appPageExposureProfiles(appBaseConfig);
    const issues = (0, app_base_contract_1.getAppVisibilityCompatibilityIssues)({
        composition,
        appDefinitions: (0, app_base_contract_1.liveAppDefinitions)(appBaseConfig.appDefinitions),
        pageExposureProfiles: profiles,
    });
    for (const route of manifest.routes) {
        const mount = (0, app_base_contract_1.mountOfRoute)(composition.mounts, route.path);
        const definition = mount && appBaseConfig.appDefinitions[mount.appId];
        if (!mount || !definition)
            continue;
        const profile = profiles.get(route.path);
        const refuse = (requirement, message) => issues.push(app_base_contract_1.AppVisibilityCompatibilityIssueSchema.parse({
            mountId: mount.mountId,
            appId: mount.appId,
            exposureProfile: profile,
            routePath: route.path,
            requirement,
            message,
        }));
        for (const actionId of route.agentActionIds) {
            const agentAction = definition.agentActions.find(candidate => candidate.id === actionId);
            const sourceKeys = (agentAction?.bindings ?? []).map(binding => (0, app_base_contract_1.mountedAppSourceKey)(mount, binding.sourceKey));
            const decision = (0, app_page_access_1.resolveAppPageAccess)({
                section: appBaseConfig.pageAccess,
                routePath: route.path,
                sourceKeys,
                principal: (0, app_page_access_1.appViewerPrincipal)('member'),
            });
            if (agentAction &&
                sourceKeys.every(sourceKey => (0, app_page_access_1.appPageCapabilityAllowed)(decision, sourceKey, 'agentAction.invoke')))
                continue;
            refuse('agentActions', `Page ${route.path} shows Agent Action ${actionId}, but members do not act internally on its tables there`);
        }
        for (const action of manifest.actions) {
            if (action.type !== 'ownRowChange' && action.type !== 'rowGroupInsert')
                continue;
            if (!(0, app_base_contract_1.appPageLeadsCommand)({
                mounts: composition.mounts,
                appDefinitions: appBaseConfig.appDefinitions,
                routePath: route.path,
                actionId: action.id,
            }))
                continue;
            if (action.type === 'ownRowChange' && profile !== 'memberPortal') {
                refuse('ownRowChange', `Page ${route.path} changes own rows (${action.id}) but is no portal page`);
            }
            if (action.type === 'rowGroupInsert' && profile === 'internalLive') {
                refuse('rowGroupInsert', `Page ${route.path} adds row groups (${action.id}) for visitors or portal persons but runs internally`);
            }
        }
    }
    return issues;
}
/**
 * One App alone in a container, mounted at /, with the page rights it
 * recommends: what a new installation gets.
 */
function appDefinitionContainer(input) {
    const definition = app_base_contract_1.AppDefinitionSchema.parse(input.definition);
    const base = (0, app_base_config_1.createAppBaseAppBaseConfig)({ name: definition.title });
    const appComposition = app_base_contract_1.AppCompositionSchema.parse({
        ...base.appComposition,
        mounts: [
            {
                schemaVersion: 1,
                mountId: definition.appId,
                appId: definition.appId,
                slot: 'main',
                order: 0,
                routePrefix: '/',
                entryRoutePath: '/',
                navigation: true,
                bindings: {},
                props: {},
            },
        ],
    });
    return withCompiledAppArtifact({
        appBaseConfig: app_base_config_1.AppBaseConfigSchema.parse({
            ...base,
            appComposition,
            appDefinitions: { [definition.appId]: definition },
        }),
        previous: null,
        artifact: (0, app_base_contract_1.compileAppComposition)({
            composition: appComposition,
            appDefinitions: [definition],
        }),
    });
}
/**
 * The profile an App gets alone in a container: the strongest profile of its
 * pages under the rights it recommends.
 */
function resolveAppDefinitionExposureProfile(input) {
    return (0, app_base_contract_1.strongestAppBaseExposureProfile)(appPageExposureProfiles(appDefinitionContainer(input)).values(), 'publicStatic');
}
/**
 * getAppPageExposureIssues for an App alone in a container with the rights it
 * recommends: inserting without a target, the Marketplace list, the install
 * preflight.
 */
function getAppDefinitionExposureIssues(input) {
    return getAppPageExposureIssues(appDefinitionContainer(input));
}
/** The German and English text of one refusal, for the server and the lists. */
function appPageExposureIssueMessages(issue) {
    const page = issue.routePath ?? '';
    switch (issue.requirement) {
        case 'requiresSecret':
            return {
                de: `Dieses App Modul nutzt geheime Zugangsdaten, daher kann die Seite ${page} nicht für Besucher offen sein.`,
                en: `This App Module uses secret-bound operations, so page ${page} cannot be open to visitors.`,
            };
        case 'agentActions':
            return {
                de: `Die Seite ${page} zeigt Agent Actions; dort müssen Mitarbeiter intern handeln.`,
                en: `Page ${page} shows Agent Actions; members have to act internally there.`,
            };
        case 'ownRowChange':
            return {
                de: `Eigene Zeilen ändern geht nur auf Portal-Seiten (${page}).`,
                en: `Changing own rows works on portal pages only (${page}).`,
            };
        case 'rowGroupInsert':
            return {
                de: `Die Seite ${page} fügt Zeilengruppen ein; dafür muss sie für Besucher oder Portal-Personen offen sein.`,
                en: `Page ${page} adds row groups, so it has to be open to visitors or portal persons.`,
            };
        case 'requiresWrite':
            return {
                de: 'Dieses App Modul schreibt Daten; keine seiner Seiten erlaubt Anlegen, Ändern oder Löschen.',
                en: 'This App Module writes data, but none of its pages allows create, update or delete.',
            };
        default:
            return {
                de: `Die Seitenrechte passen nicht zu diesem App Modul (${issue.requirement}).`,
                en: issue.message,
            };
    }
}
